import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Upload, Download, Edit, Trash2 } from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import MedicineForm from "./MedicineForm";
import MedicineBulkUpload from "./MedicineBulkUpload";
import { showSuccess, showError } from "@/utils/toastHelpers";

export default function MedicineCatalog() {
  const queryClient = useQueryClient();

  const [showMedicineForm, setShowMedicineForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Fetch medicines
  const {
    data: medicines = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["medicines", companyFilter, categoryFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (companyFilter !== "all") {
        params.append("company", companyFilter);
      }
      if (categoryFilter !== "all") {
        params.append("category", categoryFilter);
      }
      return apiClient.get(`${ENDPOINTS.MEDICINES.LIST}?${params.toString()}`);
    },
  });

  // Delete medicine mutation
  const deleteMedicine = useMutation({
    mutationFn: (id) => apiClient.delete(ENDPOINTS.MEDICINES.DELETE(id)),
    onSuccess: () => {
      queryClient.invalidateQueries(["medicines"]);
      showSuccess("Medicine deleted successfully");
    },
    onError: (error) => {
      console.error("Failed to delete medicine:", error);
      showError(
        "Failed to delete medicine",
        error.response?.data?.error || "It may be in use"
      );
    },
  });

  const handleAddMedicine = () => {
    setEditingMedicine(null);
    setShowMedicineForm(true);
  };

  const handleEditMedicine = (medicine) => {
    setEditingMedicine(medicine);
    setShowMedicineForm(true);
  };

  const handleDeleteMedicine = async (medicineId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this medicine? This will only work if the medicine is not referenced in inventory or prescriptions."
      )
    ) {
      try {
        await deleteMedicine.mutateAsync(medicineId);
      } catch (error) {
        // Error handling is done in the mutation's onError callback
      }
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent =
      "company,product,strength,dosageForm,packSize,sellingPrice,schedule,genericName,nappiCode,category,notes\n" +
      "Adcock,Amfexa,5mg,Tablets,30's,125.50,6,Dextroamphetamine,123456,medication,Example medicine\n" +
      "Pfizer,Lipitor,20mg,Tablets,30's,285.00,S4,Atorvastatin,234567,medication,Cholesterol medication\n";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "medicine_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Get unique companies for filter
  const uniqueCompanies = [...new Set(medicines.map((m) => m.company))].sort();

  const filteredMedicines = medicines.filter((medicine) => {
    const searchMatch =
      medicine.product?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medicine.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medicine.genericName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medicine.nappiCode?.toLowerCase().includes(searchTerm.toLowerCase());
    return searchMatch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            Medicine Catalog
          </h2>
          <p className="text-slate-600">
            Master list of all medicines available
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleDownloadTemplate}
            variant="outline"
            className="border-slate-300"
            title="Download CSV template (can be opened in Excel)"
          >
            <Download className="w-4 h-4 mr-2" />
            Template
          </Button>
          <Button
            onClick={() => setShowBulkUpload(true)}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            title="Upload CSV or Excel file"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Upload
          </Button>
          <Button
            onClick={handleAddMedicine}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Medicine
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search by product, company, generic name, or NAPPI code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/60 backdrop-blur-sm border-slate-200/60"
          />
        </div>

        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-md bg-white/60 backdrop-blur-sm"
        >
          <option value="all">All Companies</option>
          {uniqueCompanies.map((company) => (
            <option key={company} value={company}>
              {company}
            </option>
          ))}
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-md bg-white/60 backdrop-blur-sm"
        >
          <option value="all">All Categories</option>
          <option value="medication">Medication</option>
          <option value="supplement">Supplement</option>
          <option value="hormone">Hormone</option>
          <option value="vitamin">Vitamin</option>
        </select>
      </div>

      {/* Medicines Table */}
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-slate-200/60 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-600">
            Loading medicines...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            Error loading medicines. Please try again.
          </div>
        ) : filteredMedicines.length === 0 ? (
          <div className="p-8 text-center text-slate-600">
            No medicines found. Add your first medicine to get started.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-slate-50/80 border-b border-slate-200/60 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Strength
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Form
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Pack Size
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Generic Name
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Selling Price
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Min Stock
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/60">
                {filteredMedicines.map((medicine) => (
                  <tr key={medicine.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-sm text-slate-900">
                      {medicine.company}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">
                      {medicine.product}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {medicine.strength}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {medicine.dosageForm}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {medicine.packSize}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        S{medicine.schedule}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {medicine.genericName || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      {medicine.sellingPrice !== undefined && medicine.sellingPrice !== null ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          R {Number(medicine.sellingPrice).toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {medicine.minimumStock !== undefined && medicine.minimumStock !== null ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                          {medicine.minimumStock}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEditMedicine(medicine)}
                          className="p-1 hover:bg-blue-100 rounded text-blue-600"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteMedicine(medicine.id)}
                          className="p-1 hover:bg-red-100 rounded text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Total count */}
      {filteredMedicines.length > 0 && (
        <div className="text-sm text-slate-600 text-center">
          Showing {filteredMedicines.length} of {medicines.length} medicines
        </div>
      )}

      {/* Forms */}
      {showMedicineForm && (
        <MedicineForm
          medicine={editingMedicine}
          onClose={() => {
            setShowMedicineForm(false);
            setEditingMedicine(null);
          }}
        />
      )}

      {showBulkUpload && (
        <MedicineBulkUpload onClose={() => setShowBulkUpload(false)} />
      )}
    </div>
  );
}
